import React from 'react';
import './ConflictHoverCard.css';
import './AssignmentHoverCard.css'; /* Still needed for .premium-hover-card and .assignment-hover-card base */
import { getConflictMetaData } from '../../../utils/leaderUtils';

/**
 * ConflictHoverCard
 *
 * @param {Object} props Component props
 * @param {{ blocks: Array<{ dateRange?: string, reason: string, items?: string[] }> }|null} props.conflict Conflict data
 * @param {{ top: number, left: number }|null} props.position Screen position for the card
 * @param {Function} props.onMouseEnter Keeps the card open while hovering it
 * @param {Function} props.onMouseLeave Closes the card when leaving it
 */
export default function ConflictHoverCard({ conflict, position, title, onMouseEnter, onMouseLeave }) {
    const cardRef = React.useRef(null);
    const panelRef = React.useRef(null);
    const [measuredHeight, setMeasuredHeight] = React.useState(null);
    const [measuredSignature, setMeasuredSignature] = React.useState(null);
    const [isScrollable, setIsScrollable] = React.useState(false);
    const [isAtEnd, setIsAtEnd] = React.useState(true);

    const groupedBlocks = React.useMemo(() => {
        const blocks = conflict?.blocks || [];
        const groups = {};
        
        blocks.forEach(block => {
            // Standardize reason for grouping (strip semicolon if present from sidebar)
            const reason = (block.reason || 'Unbekannt').replace(';', '').trim();
            if (!groups[reason]) groups[reason] = { 
                label: reason, 
                blocks: [], 
                priority: block.priority ?? 99, 
                type: block.type || 'generic' 
            };
            groups[reason].blocks.push(block);
        });

        // Sort groups by priority (0 = Status, 1 = Absence, 2 = Collision, etc.)
        return Object.values(groups).sort((a, b) => a.priority - b.priority);
    }, [conflict]);

    const currentSignature = React.useMemo(() => {
        const blockSignature = (conflict?.blocks || []).map(block => {
            const dateRange = block?.dateRange || '';
            const reason = block?.reason || '';
            const detail = block?.detail || '';
            return `${dateRange}::${reason}::${detail}`;
        }).join('||');
        return `${position?.height || 0}:${position?.maxHeight || 0}:${blockSignature}`;
    }, [conflict, position?.height, position?.maxHeight]);

    // Using centralized helper from leaderUtils.js
    const getConflictMeta = getConflictMetaData;

    const measureMode = measuredSignature !== currentSignature;

    React.useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card) return;
        if (!measureMode) return;
        const nextHeight = Math.ceil(card.getBoundingClientRect().height);
        if (nextHeight > 0) {
            setMeasuredHeight(prev => (prev === nextHeight ? prev : nextHeight));
            setMeasuredSignature(currentSignature);
        }
    }, [currentSignature, measureMode]);

    React.useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;

        const syncScrollState = () => {
            const scrollable = panel.scrollHeight > panel.clientHeight + 1;
            const atEnd = !scrollable || panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2;
            setIsScrollable(scrollable);
            setIsAtEnd(atEnd);
        };

        syncScrollState();

        panel.addEventListener('scroll', syncScrollState, { passive: true });
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncScrollState) : null;
        resizeObserver?.observe(panel);

        return () => {
            panel.removeEventListener('scroll', syncScrollState);
            resizeObserver?.disconnect();
        };
    }, [currentSignature, measureMode, measuredHeight]);

    const panelMaxHeight = Math.max((position?.maxHeight || 0) - 56, 160);
    const clampHeight = Math.max(0, position?.height || 0);
    const shouldClamp = measuredHeight !== null && clampHeight > 0 && measuredHeight > clampHeight;

    if (!conflict?.blocks?.length || !position) return null;

    return (
        <div
            ref={cardRef}
            className="assignment-hover-card conflict-hover-card premium-hover-card"
            data-measure-mode={measureMode ? 'true' : 'false'}
            aria-hidden={measureMode ? true : undefined}
            style={{
                left: position.left,
                // anchor to top or bottom depending on alignment
                top: position.align === 'bottom' ? 'auto' : position.top,
                bottom: position.align === 'bottom' ? (window.innerHeight - position.top) : 'auto',
                height: measureMode ? 'auto' : (shouldClamp ? position.height : 'auto'),
                maxHeight: position.maxHeight,
                zIndex: position.zIndex || undefined,
                '--conflict-panel-max-height': `${panelMaxHeight}px`
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="assignment-hover-card__eyebrow conflict-hover-card__eyebrow">
                {title ? title.toUpperCase() : 'KONFLIKT-ÜBERSICHT'}
            </div>
            <div className="conflict-hover-card__content-wrapper" style={{ 
                position: 'relative', 
                display: 'flex', 
                flexDirection: 'column', 
                flex: 1, 
                minHeight: 0, 
                overflow: 'hidden',
                paddingBottom: isScrollable && !isAtEnd ? '40px' : '0px' // Space for the sticky hint
            }}>
                <div className="conflict-hover-card__panel" ref={panelRef} style={{ maxHeight: 'var(--conflict-panel-max-height)', overflowY: 'auto' }}>
                    <div className="conflict-feed">
                        {groupedBlocks.map((group, gIdx) => (
                            <div key={group.label} className="conflict-day-group">
                                <div className="conflict-day-header">
                                    <span className="icon">
                                        {group.type === 'status' ? '🚫' : group.type === 'absence' ? '🏖️' : '⚠️'}
                                    </span>
                                    {group.label}
                                </div>
                                <div className="conflict-day-entries">
                                    {group.blocks.map((block, bIdx) => {
                                        const meta = getConflictMeta(block.reason);
                                        
                                        return (
                                            <div 
                                                key={`${gIdx}-${bIdx}`} 
                                                className={`conflict-entry conflict-entry--${meta.type}`}
                                            >
                                                <div className="conflict-entry__header">
                                                    {block.dateRange && (
                                                        <span className="conflict-entry__date-label">
                                                            📅 {block.dateRange}
                                                            {block.detail && <span className="conflict-entry__date-separator">•</span>}
                                                        </span>
                                                    )}
                                                    {block.detail && (
                                                        <span className="conflict-entry__title conflict-entry__title--highlight">
                                                            {block.detail}
                                                        </span>
                                                    )}
                                                    {!block.detail && !block.dateRange && block.reason !== group.label && (
                                                        <span className="conflict-entry__title">
                                                            {block.reason}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {block.items && block.items.length > 0 && (
                                                    <div className="conflict-entry__items">
                                                        {block.items.map((item, i) => (
                                                            <div key={i} className="conflict-entry__item">
                                                                {item}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {isScrollable && !isAtEnd && (
                    <div className="conflict-hover-card__scroll-hint" aria-hidden="true" style={{ bottom: '12px', zIndex: 10 }}>Weiter scrollen</div>
                )}
            </div>
        </div>
    );
}
